import { Phone, Mail, MapPin } from "lucide-react";
import { PageHeader } from "@/components/site/PageHeader";
import { Impact } from "@/components/site/Impact";
import { QuoteFooter } from "@/components/site/QuoteFooter";
import { Reveal } from "@/components/site/motion";
import { ABOUT_INTRO, ABOUT_PARAS, OFFICES, HOME_STATS, CONTACT } from "@/components/site/data";
import { Seo, localBusinessLd } from "@/components/site/Seo";

export default function AboutPage() {
  return (
    <>
      <Seo
        path="/about"
        title="About Us"
        description="SolarArk Australia — leaders by design. Trusted by 30,000+ Australian homes and businesses since 2009. In-house engineering, uncompromised quality and leading warranties."
        jsonLd={localBusinessLd}
      />
      <PageHeader
        overline="About SolarArk · Est. 2009"
        title="Leaders by design."
        subtitle="Trusted by more than 30,000 Australian homes and businesses to reduce energy costs and improve building comfort through innovative, sustainable solar technology."
      />

      {/* Narrative */}
      <section data-testid="about-narrative" className="relative py-24 lg:py-32 bg-[#050505] border-y border-[#1c1c1c]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-[0.9fr_1.6fr] gap-16 lg:gap-24">
          <div className="lg:sticky lg:top-32 lg:self-start">
            <Reveal>
              <p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-6">Our Story</p>
              <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white" style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
                Better by Design.
              </h2>
              <p className="mt-6 text-zinc-400 leading-relaxed max-w-sm">{ABOUT_INTRO}</p>
            </Reveal>
          </div>

          <div className="flex flex-col">
            {ABOUT_PARAS.map((c, i) => (
              <Reveal key={c.n} delay={0.05}>
                <div className={`py-10 lg:py-12 grid grid-cols-[auto_1fr] gap-6 lg:gap-10 ${i !== 0 ? "border-t border-[#1c1c1c]" : ""}`}>
                  <span className="font-display font-light text-4xl lg:text-5xl text-[#fcc41b]/80 leading-none">{c.n}</span>
                  <div>
                    <h3 className="font-display font-semibold text-2xl lg:text-3xl text-white mb-4">{c.title}</h3>
                    <p className="text-zinc-400 text-lg leading-relaxed max-w-xl">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Impact overline="National Impact" title="Fifteen years of Australian solar engineering." stats={HOME_STATS} />

      {/* Offices */}
      <section data-testid="about-offices" className="relative py-24 lg:py-32 bg-[#050505] border-t border-[#1c1c1c]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal>
            <p className="text-xs tracking-[0.3em] uppercase text-[#fcc41b] font-display font-medium mb-5">Find Us</p>
            <h2 className="font-display font-semibold tracking-tighter leading-[0.95] text-white mb-14" style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
              Sales & support, Australia-wide.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-4">
            {OFFICES.map((o, i) => (
              <Reveal key={o.city} delay={i * 0.08}>
                <div className="rounded-3xl border border-[#262626] bg-[#0d0d0d] p-8 lg:p-10 h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="h-11 w-11 rounded-full border border-[#333] flex items-center justify-center text-[#fcc41b]"><MapPin size={18} /></span>
                    <h3 className="font-display font-semibold text-2xl text-white">{o.city}</h3>
                  </div>
                  <p className="text-zinc-400 text-lg leading-relaxed">{o.address}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 flex flex-col sm:flex-row gap-6">
              <a href={CONTACT.phoneHref} data-testid="about-phone" className="flex items-center gap-4 text-zinc-300 hover:text-[#fcc41b] transition-colors duration-300">
                <span className="h-11 w-11 rounded-full border border-[#333] flex items-center justify-center"><Phone size={18} /></span>
                {CONTACT.phone}
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="about-email" className="flex items-center gap-4 text-zinc-300 hover:text-[#fcc41b] transition-colors duration-300">
                <span className="h-11 w-11 rounded-full border border-[#333] flex items-center justify-center"><Mail size={18} /></span>
                {CONTACT.email}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <QuoteFooter blurb="Get in touch to organise an obligation-free quote, ask a question or provide feedback — our team is happy to help." />
    </>
  );
}
